import { useState, type ReactNode } from "react";

import { useCollectionStructure } from "../api/documents";
import { useCollections } from "../api/queries";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { Field } from "../ui/Field";
import { flattenNodes, subtreeIds } from "./tree";

export interface Destination {
  collectionId: string;
  parentDocumentId: string | null;
}

export interface DestinationDialogProps {
  open: boolean;
  title: string;
  description?: string | undefined;
  confirmLabel: string;
  busy?: boolean;
  error?: string | undefined;
  /** Pre-selected collection; the first readable one is used when absent. */
  initialCollectionId?: string | null | undefined;
  /** A document may not be moved into itself or its own descendants. */
  excludeDocumentId?: string | undefined;
  onConfirm: (destination: Destination) => void;
  onClose: () => void;
}

/**
 * "Where should this live?" — the one dialog behind publishing a draft and
 * behind moving a document, because both questions are the same pair of
 * choices: which collection, and under which document inside it.
 */
export function DestinationDialog({
  open,
  title,
  description,
  confirmLabel,
  busy = false,
  error,
  initialCollectionId,
  excludeDocumentId,
  onConfirm,
  onClose,
}: DestinationDialogProps): ReactNode {
  const collections = useCollections(open);
  // The selection is only stored once the user makes one; until then the
  // default is derived, so it follows the collections arriving without an
  // effect that would fight a choice already made.
  const [chosen, setChosen] = useState<string | null>(null);
  const [parentDocumentId, setParentDocumentId] = useState<string>("");
  const collectionId = chosen ?? initialCollectionId ?? collections.data?.[0]?.id ?? "";

  const structure = useCollectionStructure(collectionId, open && collectionId !== "");
  const nodes = structure.data ?? [];
  const excluded =
    excludeDocumentId === undefined ? new Set<string>() : subtreeIds(nodes, excludeDocumentId);
  const candidates = flattenNodes(nodes).filter((entry) => !excluded.has(entry.node.id));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      {...(description === undefined ? {} : { description })}
      width="sm"
    >
      <div className="flex flex-col gap-3">
        <Field label="Collection">
          {(id) => (
            <select
              id={id}
              value={collectionId}
              onChange={(event) => {
                setChosen(event.target.value);
                setParentDocumentId("");
              }}
              className="w-full rounded-md border border-line bg-raised px-3 py-2 text-sm"
            >
              {collections.data === undefined || collections.data.length === 0 ? (
                <option value="">No collections available</option>
              ) : (
                collections.data.map((collection) => (
                  <option key={collection.id} value={collection.id}>
                    {collection.name}
                  </option>
                ))
              )}
            </select>
          )}
        </Field>

        <Field label="Nest under" description="Leave at the top level to place it at the root.">
          {(id) => (
            <select
              id={id}
              value={parentDocumentId}
              onChange={(event) => {
                setParentDocumentId(event.target.value);
              }}
              className="w-full rounded-md border border-line bg-raised px-3 py-2 text-sm"
            >
              <option value="">Top level</option>
              {candidates.map((entry) => (
                <option key={entry.node.id} value={entry.node.id}>
                  {`${"— ".repeat(entry.depth)}${entry.node.title === "" ? "Untitled" : entry.node.title}`}
                </option>
              ))}
            </select>
          )}
        </Field>

        {error !== undefined && error !== "" && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={busy || collectionId === ""}
            onClick={() => {
              onConfirm({
                collectionId,
                parentDocumentId: parentDocumentId === "" ? null : parentDocumentId,
              });
            }}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
